import { Box, ButtonBase, CircularProgress, IconButton } from '@mui/material'
import { BrokenImageOutlined, ImageNotSupportedOutlined } from '@mui/icons-material'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import React, { lazy, Suspense, useState } from 'react'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useToggle } from '../../generic/hooks/useToggle'

// Lazy-loaded: SmartDocumentViewer pulls in three.js (heavy, ESM) via its 3D branch. Loading it only
// when the modal opens keeps three.js out of the static import graph of the list screens (table/
// lines/OFs) that embed the thumbnail, and off the bundle until a user actually enlarges an image.
const SmartDocumentViewer = lazy(() =>
  import('../document/SmartDocumentViewer').then((module) => ({ default: module.SmartDocumentViewer }))
)

// Fixed dimensions keep the table layout stable whether a row has an image or not (CA7 / S30, S31).
const THUMBNAIL_WIDTH = 56
const THUMBNAIL_HEIGHT = 56
const COLUMN_WIDTH = '4.5rem'

// Preview extensions the browser can render directly. Sun also supports webp, so it is included here
// for the enlarged preview even though it is not part of SmartBrowserDocumentViewerImageExtensions.
const previewableImageExtensions = ['png', 'gif', 'jpg', 'jpeg', 'bmp', 'svg', 'webp']

const isPreviewableImage = (extension) => {
  if (!extension) {
    return false
  }
  return previewableImageExtensions.includes(extension.toLowerCase())
}

// Extensions allowed as a part-reference main image: only formats Sun both accepts and can generate a
// thumbnail for. webp (no server-side thumbnail, ImageIOThumbnailGenerator) and svg (rejected by
// Sun's ImageExtension) are intentionally excluded so a main image always yields a real thumbnail.
const mainImageAllowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp']

const isAllowedMainImage = (extension) => {
  if (!extension) {
    return false
  }
  return mainImageAllowedExtensions.includes(extension.toLowerCase())
}

/**
 * Reusable main-image thumbnail for the part reference list screens.
 * The thumbnail itself is resolved in a single batch call by the parent (useMainImageResolution →
 * `partReferences/mainImages/thumbnails`) and passed in as base64, so this component no longer issues
 * one HTTP request per row. Renders a fixed-size thumbnail when the reference has one, a controlled
 * placeholder when it does not (S34), and a non-blocking fallback when the base64 cannot be rendered
 * (S35 / S36). Clicking an existing image opens a read-only modal with the enlarged image (S32),
 * loaded from the original document via `mainImageInfo` (uuid/extension) — so a GIF stays animated in
 * the viewer even though the list thumbnail is a static JPEG. Visualization is never gated by document
 * permissions (S37) — the parent simply renders it.
 *
 * @param {Object} props Props object
 * @param {string} [props.thumbnail] Base64 JPEG of the thumbnail (no data-URI prefix). Empty = no image.
 * @param {Object} props.mainImageInfo Batch-resolved info: { hasMainImage, documentId, uuid, fileName, extension }
 */
const MainImageThumbnail = ({ mainImageInfo = {}, thumbnail = '' }) => {
  const { t_ } = useSmartTranslation()
  const [thumbError, setThumbError] = useState(false)
  const [open, setOpen] = useState(false)
  // Toggled by the fullscreen button to enlarge the single image in the modal (S32).
  const [requestFullScreen, onRequestFullscreen] = useToggle()

  const uuid = mainImageInfo?.uuid || ''
  const fileName = mainImageInfo?.fileName || ''
  const extension = mainImageInfo?.extension || ''

  const containerSx = {
    width: COLUMN_WIDTH,
    height: THUMBNAIL_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  }

  // Placeholder: no main image for this reference (id not in the batch thumbnail map) (S34).
  if (!thumbnail) {
    return (
      <Box sx={containerSx}>
        <Box
          aria-label={t_('No main image')}
          role='img'
          sx={{
            width: THUMBNAIL_WIDTH,
            height: THUMBNAIL_HEIGHT,
            borderRadius: 1,
            backgroundColor: 'grey.800',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ImageNotSupportedOutlined sx={{ color: 'grey.500' }} fontSize='small' />
        </Box>
      </Box>
    )
  }

  // Non-blocking fallback when the base64 thumbnail cannot be rendered (S35 / S36).
  if (thumbError) {
    return (
      <Box sx={containerSx}>
        <Box
          aria-label={t_('Image not available')}
          role='img'
          sx={{
            width: THUMBNAIL_WIDTH,
            height: THUMBNAIL_HEIGHT,
            borderRadius: 1,
            backgroundColor: 'grey.800',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <BrokenImageOutlined sx={{ color: 'grey.500' }} fontSize='small' />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={containerSx}>
      <ButtonBase
        onClick={() => setOpen(true)}
        aria-label={t_('View main image')}
        sx={{
          width: THUMBNAIL_WIDTH,
          height: THUMBNAIL_HEIGHT,
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: 'grey.900',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          component='img'
          src={'data:image/jpeg;base64,' + thumbnail}
          alt={fileName || t_('Main image')}
          onError={() => setThumbError(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </ButtonBase>
      <SmartDialog
        setOpen={open}
        accept={false}
        cancel={false}
        close
        closeCallback={() => setOpen(false)}
        minWidth='60vw'
        paperHeight='75vh'
        growContainerHeight
        renderComponent={
          // Read-only viewer of THIS main image only (S32) — not the whole document list. The
          // fullscreen button enlarges it. Lazy-loaded to keep three.js out of the list screens.
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: '50vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={onRequestFullscreen} aria-label={t_('Fullscreen')}>
                <FullscreenIcon />
              </IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <Suspense fallback={<CircularProgress />}>
                <SmartDocumentViewer
                  documentUUID={uuid}
                  extension={extension}
                  documentName={fileName}
                  requestFullScreen={requestFullScreen}
                />
              </Suspense>
            </Box>
          </Box>
        }
      />
    </Box>
  )
}

export { MainImageThumbnail, COLUMN_WIDTH, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT, isPreviewableImage, isAllowedMainImage, mainImageAllowedExtensions }
export default MainImageThumbnail
